import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ButtonParamType, DefaultButtonStyle } from 'src/components/button/button';
import { ReducerContext } from 'src/context/reducer-context';
import ImageCapture from 'src/components/image-capture/image-capture';
import { CameraNotEnabled } from 'src/assets/images';
import ButtonGroup, {
  ButtonConfig,
  LayoutType,
} from 'src/features/common/components/button-group/revamp-button-group';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Modal, HeadingOne } from 'src/features/common';
import { Base, contacts, publicSiteUrl } from 'src/common';
import { notify } from '../toast/toast';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import './revamp-webcam-checker.scss';

type Props = {
  onShow?: () => void;
  onClick?: (action: "i'll be back" | "i've enabled it") => void;
};

export default function WebcamChecker(props: Props) {
  const { dispatch } = useContext(ReducerContext);
  const [showErrorModalMiniApp, setShowErrorModalMiniApp] = useState<boolean>(true);

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);
  const isMobile = useIsMobile();
  enum WizardMessageNames {
    NAVIGATE_TO_NEXT_STEP = 'NAVIGATE_TO_NEXT_STEP',
    NAVIGATE_TO_PREVIOUS_STEP = 'NAVIGATE_TO_PREVIOUS_STEP',
    SHOW_PAGE_LOADER = 'SHOW_PAGE_LOADER',
    HIDE_PAGE_LOADER = 'HIDE_PAGE_LOADER',
  }

  enum WebcamCheckerStatus {
    NOT_STARTED_YET,
    IN_PROGRESS,
    WEBCAM_FOUND,
    WEBCAM_ERROR,
  }

  const [webcamCheckerStatus, setWebcamCheckerStatus] = useState<WebcamCheckerStatus>(
    WebcamCheckerStatus.NOT_STARTED_YET,
  );

  useEffect(() => {
    if (props.onShow) {
      props.onShow();
    }
  }, []);

  useEffect(() => {
    switch (webcamCheckerStatus) {
      case WebcamCheckerStatus.NOT_STARTED_YET:
      case WebcamCheckerStatus.IN_PROGRESS:
        dispatch(WizardMessageNames.SHOW_PAGE_LOADER);
        break;
      case WebcamCheckerStatus.WEBCAM_ERROR:
        dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
        break;
      case WebcamCheckerStatus.WEBCAM_FOUND:
        dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
        break;
    }
  }, [webcamCheckerStatus]);

  const buildModalButtonsMiniApp = () => {
    const myCameraIsEnabled: ButtonConfig = {
      label: 'Camera already enabled',
      automationId: 'Revamp_Webcam_Checker_Modal_My_Camera_Is_Enabled',
      buttonStyle: DefaultButtonStyle().hasSecondaryAction().isMediumSize().hasDarkMode().build(),
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          setShowErrorModalMiniApp(false);
        },
      },
      className: 'button',
    };

    const enableNow: ButtonConfig = {
      automationId: 'Revamp_Webcam_Checker_Modal_Enable_Now',
      label: 'Enable camera now',
      buttonStyle: DefaultButtonStyle().hasPrimaryAction().hasDarkMode().isMediumSize().build(),
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          if (inMiniApp) {
            MiniAppEvents.sendMessage({
              messageType: 'camera_permission',
            });
            setShowErrorModalMiniApp(false);

            MiniAppEvents.listenForMessage((observer) => {
              const message = MiniAppEvents.readMessage();
              if (message.type === 'camera_permission') {
                observer.disconnect();
                if (message.data) {
                  notify({
                    children: `Your camera has been enabled.`,
                    autoClose: 5000,
                  });
                }
              }
            });
          }
        },
      },
      className: 'button',
    };

    return (
      <ButtonGroup buttons={[myCameraIsEnabled, enableNow]} layoutType={LayoutType.CENTERED} />
    );
  };

  const buildModalButtons = () => {
    const buttonBeBackLater: ButtonConfig = {
      automationId: 'Revamp_Order_Outcome_Ill_Be_Back_Button',
      label: "I'll be back",
      buttonStyle: DefaultButtonStyle().hasSecondaryAction().isMediumSize().hasDarkMode().build(),
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          if (props.onClick) {
            props.onClick("i'll be back");
          }
          window.location.href = publicSiteUrl;
        },
      },
      className: 'button',
    };

    const buttonEnable: ButtonConfig = {
      automationId: 'Revamp_Order_Outcome_Ive_Enabled_It_Button',
      label: "I've enabled it",
      buttonStyle: DefaultButtonStyle().hasPrimaryAction().hasDarkMode().isMediumSize().build(),
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          if (props.onClick) {
            props.onClick("i've enabled it");
          }
          window.location.reload();
        },
      },
      className: 'button',
    };

    return (
      <ButtonGroup buttons={[buttonEnable, buttonBeBackLater]} layoutType={LayoutType.CENTERED} />
    );
  };

  return inMiniApp ? (
    <div className="RevampWebcamChecker">
      <Modal visible={showErrorModalMiniApp} size="small" onClose={() => window.location.reload()}>
        <Base overflowY="auto">
          <HeadingOne>Please enable your {isMobile ? 'camera' : 'webcam'}</HeadingOne>
          <section>
            <p className="greyed-text">
              Your selfie helps us to protect you from identity fraud and verify your identity with
              the Department of Home Affairs. Please enable your camera to continue.
            </p>
            <hr />
            {buildModalButtonsMiniApp()}
          </section>
        </Base>
      </Modal>
    </div>
  ) : (
    <div className="RevampWebcamChecker">
      <ImageCapture
        onDeviceAccessed={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_FOUND)}
        onDeviceAccessDenied={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_ERROR)}
        onDeviceNotFound={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_ERROR)}
      />

      <Modal
        visible={webcamCheckerStatus === WebcamCheckerStatus.WEBCAM_ERROR}
        size="medium"
        onClose={() => window.location.reload()}
      >
        <Base overflowY="auto">
          <HeadingOne>Please enable your {isMobile ? 'camera' : 'webcam'}</HeadingOne>
          <section>
            <p className="greyed-text">You'll need a working webcam to use our site...</p>
            {buildModalButtons()}
            <hr />
            <p>Still having issues?</p>
            <ul>
              <li>Close any programs like Teams or Zoom that might be using your webcam</li>
              <li>Check that your camera is enabled in your browser settings</li>
              <li>
                Enable your camera ( <img src={CameraNotEnabled} alt="Camera not enabled" />) by
                clicking the icon in your browser url bar
              </li>
              <li className="bolded-item">
                Alternatively, contact our Support Centre:{' '}
                <a
                  href={contacts.numberUrl}
                  automationid="Revamp_Order_Outcome_Support_Centre_Phone_Number"
                >
                  {contacts.number}
                </a>
              </li>
            </ul>
          </section>
        </Base>
      </Modal>
    </div>
  );
}
